import { useEffect, useRef, useState } from "react";
import { isMobile } from "react-device-detect";
import { useNavigate } from "react-router-dom";
import { OlgaEnvironmentsAPI } from "../providers/OlgaEnvironmentsAPI";
import { StandaloneCouponsAPI } from "../providers/StandaloneCouponsAPI";
import { Button } from "../shared/components/Button";
import { Empty } from "../shared/components/Empty";
import { Loading } from "../shared/components/Loading";
import { Title } from "../shared/components/Title";
import { If } from "../shared/components/control-statements";
import { Icon } from "../shared/components/core/Icon";
import { BenefitFormDrawer } from "../shared/containers/beneficios/BenefitFormDrawer";
import { BenefitList } from "../shared/containers/beneficios/BenefitList";
import FoldersDrawer from "../shared/containers/beneficios/FoldersDrawer";
import FoldersList from "../shared/containers/beneficios/FoldersList";
import { useIsInViewport } from "../shared/hooks/useIsInViewport";

import styles from "../styles/beneficios/BenefitList.module.scss";
import { BenefitFormDrawerOld } from "shared/containers/beneficios/BenefitFormDrawerOld";
import { Environment } from "shared/core/Environment";

export default function Beneficios() {
  const [benefits, setBenefits] = useState([]);
  const [hasError, setHasError] = useState(false);
  const [loading, setLoading] = useState(true);

  const [hasNext, setHasNext] = useState(true);
  const [page, setPage] = useState(0);
  const [formVisible, setFormVisible] = useState(false);
  const [benefitToEdit, setBenefitToEdit] = useState(null);
  const [openCreateFolder, setOpenCreateFolder] = useState(false);

  const pageIndexMapRef = useRef({});

  const [folders, setFolders] = useState([]);

  const loadingRef = useRef(null);
  const isVisible = useIsInViewport(loadingRef);

  const loadFolders = async () => {
    const environmentConfigurations =
      await OlgaEnvironmentsAPI.getConfigurations();

    const folders = environmentConfigurations.getData({})?.coupon_folders || [];

    if (environmentConfigurations.getCode(500) != 200) {
      setHasError(true);
      return;
    }

    setFolders(folders);
  };

  const navigate = useNavigate();

  const refreshItem = async (id) => {
    const itemPage = pageIndexMapRef.current[id];
    if (itemPage == null) return;

    const response = await StandaloneCouponsAPI.getStandaloneCoupons(
      "ACTIVATED",
      null,
      itemPage
    );

    const { items } = response.getData({
      items: [],
      next: { page: -1, limit: 12 },
    });

    const updated = items.find((it) => it.id === id);

    if (!updated) return;

    setBenefits((prev) =>
      prev.map((b) => {
        return b.id === id ? updated : b;
      })
    );
  };

  const loadBenefits = async (refresh) => {
    setLoading(true);

    const pageToLoad = refresh ? 0 : page;

    const response = await StandaloneCouponsAPI.getStandaloneCoupons(
      "ACTIVATED",
      null,
      pageToLoad
    );

    const benefitsPage = response.getData({
      items: [],
      next: { page: -1, limit: 12 },
    });

    benefitsPage.items.forEach((item) => {
      pageIndexMapRef.current[item.id] = pageToLoad;
    });

    let newBenefitsPage;

    if (refresh) {
      newBenefitsPage = [...benefitsPage.items];
    } else {
      newBenefitsPage = [...benefits, ...benefitsPage.items];
    }

    setBenefits(newBenefitsPage);

    const hasNextPage = benefitsPage.next.page !== -1;
    setHasNext(hasNextPage);

    if (hasNextPage) {
      setPage(benefitsPage.next.page);
    }

    setLoading(false);
  };

  useEffect(() => {
    loadFolders();
    if (hasNext && isVisible) {
      loadBenefits();
    }
  }, [isVisible, page]);

  return hasError ? (
    <Empty>
      <Icon name="exclamation-circle" size="4x" light />
      <p>Ops!</p>
      <span>
        Ocorreu um erro ao carregar essa página. Verifique as configurações e
        tente novamente!
      </span>
    </Empty>
  ) : (
    <>
      <Title
        title="Cupons"
        backTo={{
          url: "/fidelizacao-e-marketing",
          name: "Fidelização e Marketing",
        }}
      >
        <Button
          className={isMobile ? "reverse w-100 mb-15" : ""}
          design="default-icon"
          onClick={() => setOpenCreateFolder(true)}
        >
          <Icon name="fa-folder-open" light />
        </Button>

        <Button
          className={isMobile ? "reverse w-100 mb-15" : "ml-10"}
          design="default-icon"
          onClick={() => navigate("/beneficios/arquivados")}
        >
          <Icon name="fa-box-archive" />
        </Button>

        <Button
          design="primary"
          className={isMobile ? "reverse w-100 mt-15" : "ml-10"}
          onClick={() => setFormVisible(true)}
        >
          Criar cupom
        </Button>
      </Title>

      {folders.length > 0 && <FoldersList folders={folders} />}

      {benefits.length > 0 && (
        <>
          <p className={styles.label}>Desagrupados ({benefits.length})</p>
          <BenefitList
            benefits={benefits}
            onEditBenefit={(c) => {
              setBenefitToEdit(c);
              setFormVisible(true);
            }}
            onLoadBenefits={() => loadBenefits("refresh")}
          />
        </>
      )}

      <If cond={hasNext}>
        <div className={styles.loading}>
          <Loading />
        </div>
        <div ref={loadingRef}></div>
      </If>

      <If cond={benefits.length == 0 && !hasNext && !loading}>
        <Empty>
          <Icon name="ticket-simple" regular size="4x" />
          <p>Não há cupons avulsos cadastrados.</p>
          <span>
            Por meio desta ferramenta, será possível cadastrar cupons não
            vinculados a campanhas.
          </span>
          <Button
            design="primary"
            onClick={() => {
              setBenefitToEdit(null);
              setFormVisible(true);
            }}
          >
            Criar cupom
          </Button>
        </Empty>
      </If>

      <BenefitFormDrawer
        visible={formVisible && Environment.id === 51}
        benefitToEdit={benefitToEdit}
        onSave={(benefitId) => {
          if (benefitId) refreshItem(benefitId);
          else loadBenefits("refresh");
        }}
        onClose={() => {
          setFormVisible(false);
          setBenefitToEdit(null);
        }}
      />

      <BenefitFormDrawerOld
        visible={formVisible && Environment.id !== 51}
        benefitToEdit={benefitToEdit}
        onSave={(benefitId) => {
          if (benefitId) refreshItem(benefitId);
          else loadBenefits("refresh");
        }}
        onClose={() => {
          setFormVisible(false);
          setBenefitToEdit(null);
        }}
      />

      <FoldersDrawer
        visible={openCreateFolder}
        onClose={() => {
          setOpenCreateFolder(false);
          loadFolders();
        }}
        onLoadFolders={loadFolders}
        onLoadCoupons={() => loadBenefits("refresh")}
      />
    </>
  );
}
